import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, Box, Card, CardContent, CardActions, Divider, Grid, Button } from "@mui/material";
import { LiveTv, VideoCall } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface StreamingOptionsDialogProps {
    open: boolean;
    onClose: () => void;
    onChooseRTMP: () => void;
    onChooseLiveStudio: () => void;
    eventTitle?: string;
    eventDescription?: string;
}

function StreamingOptionsDialog({
    open,
    onClose,
    onChooseRTMP,
    onChooseLiveStudio,
    eventTitle,
    eventDescription
}: StreamingOptionsDialogProps) {
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="center" p={1}>
                    <LiveTv sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                    <Typography variant="h5" component="div">
                        {t('Choose Streaming Method')}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <DialogContentText align="center" paragraph>
                    {t('How would you like to stream this event?')}
                </DialogContentText>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card
                            variant="outlined"
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                '&:hover': {
                                    boxShadow: 3,
                                    borderColor: 'primary.main'
                                }
                            }}
                            onClick={onChooseRTMP}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                                    <VideoCall sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
                                    <Typography variant="h6" gutterBottom>
                                        {t('RTMP Tools')}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {t('Use professional streaming software like OBS, Larix, or other RTMP tools')}
                                    </Typography>
                                </Box>
                            </CardContent>
                            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                                <Button variant="contained" color="primary">
                                    {t('Use RTMP Tools')}
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card
                            variant="outlined"
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                '&:hover': {
                                    boxShadow: 3,
                                    borderColor: 'secondary.main'
                                }
                            }}
                            onClick={onChooseLiveStudio}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                                    <LiveTv sx={{ fontSize: 48, mb: 2, color: 'secondary.main' }} />
                                    <Typography variant="h6" gutterBottom>
                                        {t('Live4.tv Studio')}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {t('Use our built-in browser-based studio for easy streaming')}
                                    </Typography>
                                </Box>
                            </CardContent>
                            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                                <Button variant="contained" color="secondary">
                                    {t('Use Live Studio')}
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit">
                    {t('Cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default StreamingOptionsDialog;
